import { motion } from "framer-motion";
import { ArrowRight, Calendar, Phone } from "lucide-react";
import { BRAND, IMAGES } from "@/lib/constants";

export default function BookCta() {
  return (
    <section
      id="book"
      data-testid="book-cta-section"
      className="relative bg-[#111111] text-white overflow-hidden"
    >
      {/* Ambient gold glow */}
      <div className="absolute -top-40 -right-32 w-[40rem] h-[40rem] glow-gold pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] glow-gold pointer-events-none" />

      {/* Huge editorial backdrop word */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none opacity-[0.05]">
        <span
          className="font-serif text-[22vw] leading-none italic font-light"
          style={{ fontFamily: "Fraunces, serif" }}
        >
          Samtale
        </span>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 sm:pt-24 lg:pt-28 pb-0 lg:pb-0">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          {/* LEFT: copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="lg:col-span-7 order-2 lg:order-1 lg:pb-28"
          >
            <p className="overline mb-5 text-[10px] sm:text-xs" style={{ color: "#D4AF37" }}>
              <span className="inline-block w-8 sm:w-10 h-px bg-[#D4AF37] align-middle mr-3 sm:mr-4" />
              Lad os tage en snak
            </p>

            <h2
              className="font-serif text-[2.5rem] sm:text-6xl lg:text-[5rem] text-white font-light tracking-tight leading-[1.02]"
              style={{ fontFamily: "Fraunces, serif" }}
            >
              Book en{" "}
              <em className="italic text-[#D4AF37] font-light">uforpligtende</em>{" "}
              samtale.
            </h2>

            <p className="mt-6 max-w-2xl text-white/70 text-base sm:text-lg font-light leading-relaxed">
              30 minutter, online eller telefon. Jeg gennemgår din bolig, dit
              indtjeningspotentiale og hvordan vi tager hånd om driften - uden
              du forpligter dig til noget.
            </p>

              <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 max-w-xl">
                {[
                  "Højere belægning gennem målrettet markedsføring",
                  "Dynamisk prissætning der maksimerer din indtjening",
                  "Vi håndterer al gæstekommunikation 24/7",
                  "Ét samlet overblik - du følger udviklingen live",
                ].map((b) => (
                <li
                  key={b}
                  className="flex items-start gap-3 text-white/85 text-sm font-light leading-relaxed"
                >
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-5">
              <a
                href={BRAND.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="book-cta-calendly-btn"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#111111] px-5 sm:px-7 py-3.5 sm:py-4 group hover:bg-white transition-colors duration-500 max-w-full"
              >
                <Calendar size={18} strokeWidth={1.5} className="shrink-0" />
                <span className="text-[11px] sm:text-sm tracking-[0.12em] sm:tracking-[0.15em] uppercase font-medium leading-tight">
                  Book gratis møde
                </span>
                <ArrowRight
                  size={16}
                  strokeWidth={1.5}
                  className="shrink-0 transition-transform duration-500 group-hover:translate-x-1"
                />
              </a>
              <a
                href={`tel:${BRAND.phoneRaw}`}
                data-testid="book-cta-phone-link"
                className="inline-flex items-center gap-2 text-xs tracking-[0.18em] uppercase text-white/60 hover:text-[#D4AF37] transition-colors"
              >
                <Phone size={14} strokeWidth={1.5} />
                <span>eller ring {BRAND.phone}</span>
              </a>
            </div>
          </motion.div>

          {/* RIGHT: editorial portrait - full natural height, breaks bottom of section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 order-1 lg:order-2 relative flex justify-center lg:justify-end items-end"
            data-testid="book-cta-portrait-wrap"
          >
            <div className="relative w-full max-w-[280px] sm:max-w-[320px] mx-auto">
              {/* Outer gold ring backdrop */}
              <div className="absolute inset-0 rounded-full border border-[#D4AF37]/30 scale-[1.06] pointer-events-none" />
              {/* Soft gold glow */}
              <div className="absolute inset-0 rounded-full bg-[#D4AF37]/12 blur-2xl scale-[1.1] pointer-events-none" />

              {/* Circular portrait - head shown clearly */}
                <div
                  className="relative aspect-square rounded-full overflow-hidden bg-gradient-to-b from-[#1f1f1f] via-[#141414] to-[#0a0a0a] border border-[#D4AF37]/40 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.7)] flex items-end justify-center"
                  data-testid="book-cta-portrait-circle"
                >
                  <img
                    src={IMAGES.portrait}
                    alt="Gustav Gudiksen Nielsen - CEO & Founder, Gudiksen Stays"
                    className="h-[92%] w-auto object-contain object-bottom"
                    data-testid="book-cta-portrait"
                  />
                </div>

              {/* Floating editorial label - bottom */}
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.4 }}
                className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-[#111111] px-5 py-2.5 z-10 shadow-2xl whitespace-nowrap"
              >
                <div className="text-[9px] tracking-[0.32em] uppercase opacity-70 text-center">
                  CEO & Founder
                </div>
                <div
                  className="font-serif text-base sm:text-lg leading-none mt-1 font-normal text-center"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  Gustav Gudiksen <em className="italic">Nielsen</em>
                </div>
              </motion.div>

              {/* Top right small badge */}
              <motion.div
                initial={{ opacity: 0, x: 16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.55 }}
                className="absolute -top-2 -right-2 sm:-right-4 bg-white text-[#111111] px-3 py-2 z-10 shadow-xl"
              >
                <div className="text-[9px] tracking-[0.3em] uppercase text-[#C9A227]">
                  30 min
                </div>
                <div
                  className="font-serif text-sm italic leading-none mt-1"
                  style={{ fontFamily: "Fraunces, serif" }}
                >
                  uforpligtende
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
